import { defineStore, type _GettersTree } from 'pinia';
import type {
    CompanyStateType, CompanyActionsType, CompanyGettersType,
} from '@/types/stores/CompanyStoreTypes';
import type { CompanyType } from '@/types/CompanyType';
import type { CrudStoreConfigType } from '@/types/CrudStoreConfigType';
import {
    createCrudActions,
    createCrudGetters,
    createCrudState,
    createFetchValueSuggestionsAction,
} from '@/utils/storeUtils';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';

const createEmptyCompany = (): CompanyType => ({
    id: -1,
    name: '',
    currency: 'EUR',
    locale: 'en',
    tenant_name: '',
    tenant_collation: '',
    tenant_id: -1,
    primary_color: null,
    secondary_color: null,
    logo_url: null,
    logo_file: null,
    description: null,
    integrator: null,
    integrator_id: null,
});

const companyConfig: CrudStoreConfigType<CompanyType> = {
    endpoint: '/companies',
    createEmptyItem: createEmptyCompany,
    headers: [
        { key: 'tenant_id', title: 'company.tenantId' },
        { key: 'name', title: 'common.name' },
        { key: 'locale', title: 'company.defaultLanguage', sortable: false },
        { key: 'currency', title: 'company.defaultCurrency', sortable: false },
        { key: 'tenant_name', title: 'company.tenantName', sortable: false },
        { key: 'tenant_collation', title: 'company.tenantCollation', sortable: false },
        { key: 'integrator.name', title: 'common.integrator', sortable: false },
    ],
    successMessages: {
        created: 'messages.companyCreated',
        updated: 'messages.companyUpdated',
        deleted: 'messages.companyDeleted',
    },
    transformForCreate: (data: CompanyType) => {
        const { tenant_id: _tenant_id, id: _id, ...createData } = data;

        return createData;
    },
    transformForUpdate: (data: CompanyType) => data,
    sortBy: [{ key: 'name', order: 'asc' }],
    duplicationConfig: { fieldsToCopy: ['name', 'tenant_name', 'currency', 'locale'] },
    autocompletePrefix: 'companies',
};

export const useCompanyStore = defineStore<
    'company',
    CompanyStateType,
    CompanyGettersType & _GettersTree<CompanyStateType>,
    CompanyActionsType
>('company', {
    state: () => ({ ...createCrudState(companyConfig)() }),
    getters: { ...createCrudGetters<CompanyType>() },
    actions: {
        ...createCrudActions(companyConfig),
        fetchValueSuggestions: createFetchValueSuggestionsAction(companyConfig),

        async updateItem(): Promise<boolean> {
            this.isLoading = true;
            let result: boolean = false;

            const data = new FormData();
            data.append('_method', 'PUT'); // Laravel method spoofing for FormData
            data.append('name', this.editedItem.name);
            data.append('currency', this.editedItem.currency);
            data.append('locale', this.editedItem.locale);
            data.append('tenant_name', this.editedItem.tenant_name);

            if (this.editedItem.tenant_collation) {
                data.append('tenant_collation', this.editedItem.tenant_collation);
            }

            data.append('primary_color', this.editedItem.primary_color || '');
            data.append('secondary_color', this.editedItem.secondary_color || '');
            data.append('description', this.editedItem.description || '');
            if (this.editedItem.integrator_id) {
                data.append('integrator_id', this.editedItem.integrator_id.toString());
            } else {
                data.append('integrator_id', '');
            }
            if (this.editedItem.logo_file) {
                data.append('logo', this.editedItem.logo_file);
            } else if (this.editedItem.logo_url === null) {
                data.append('logo_url', '');
            }

            await handleRequest({
                method: RequestMethodEnum.Post,
                endpoint: `${companyConfig.endpoint}/${this.editedItem.id}`,
                data,
                successMessageKey: companyConfig.successMessages.updated,
                onSuccess: response => {
                    this.editedItem = response.data.data;
                    this.items = this.items.map(item => (item.id === this.editedItem.id ? this.editedItem : item));
                    this.editedItem.logo_file = null;
                    result = true;
                },
                onError: () => {
                    result = false;
                },
            });

            this.isLoading = false;

            return result;
        },
    },
});
